/**
 * WCAG 2.x contrast and the tenant palette (ADR D17). A tenant's primary color replaces pear-700, but buttons put white
 * text on it, so a color that can't carry white text at 4.5:1 is darkened, in small lightness steps, until it can.
 */

// Mirrors --pear-700, --pear-500 and --pear-50 in tokens.css (contrast.test.ts fails if they drift): the tenant palette
// is computed in JS, which cannot read the stylesheet.
export const PEAR_700 = '#4A5A12';
export const PEAR_500 = '#6E7F1C';
export const PEAR_50 = '#EEF3D2';
export const WHITE = '#FFFFFF';
export const MIN_CONTRAST = 4.5;

const HEX = /^#([0-9a-f]{6})$/i;

export function isHexColor(value: string): boolean {
  return HEX.test(value);
}

export function hexToRgb(hex: string): [number, number, number] {
  const m = HEX.exec(hex);
  if (!m) throw new Error(`not a #RRGGBB color: ${hex}`);
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function rgbToHex([r, g, b]: [number, number, number]): string {
  const c = (v: number) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

/** Relative luminance (WCAG 2.x). */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === rn ? (gn - bn) / d + (gn < bn ? 6 : 0) : max === gn ? (bn - rn) / d + 2 : (rn - gn) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb([h, s, l]: [number, number, number]): [number, number, number] {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const hue = (p: number, q: number, t: number) => {
    const u = t < 0 ? t + 1 : t > 1 ? t - 1 : t;
    if (u < 1 / 6) return p + (q - p) * 6 * u;
    if (u < 1 / 2) return q;
    if (u < 2 / 3) return p + (q - p) * (2 / 3 - u) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue(p, q, h + 1 / 3) * 255, hue(p, q, h) * 255, hue(p, q, h - 1 / 3) * 255];
}

/** Darkens a color (keeping hue and saturation) until `on` text reaches `min` contrast against it. */
export function darkenForText(hex: string, on = WHITE, min = MIN_CONTRAST): string {
  let color = hex.toUpperCase();
  const [h, s, start] = rgbToHsl(hexToRgb(color));
  for (let l = start; contrast(color, on) < min && l > 0; ) {
    l = Math.max(0, l - 0.01);
    color = rgbToHex(hslToRgb([h, s, l]));
  }
  return color;
}

/** Mixes `hex` with `with` by `amount` of `hex` (0..1). */
export function mix(hex: string, amount: number, withHex = WHITE): string {
  const a = hexToRgb(hex);
  const b = hexToRgb(withHex);
  return rgbToHex([0, 1, 2].map((i) => a[i]! * amount + b[i]! * (1 - amount)) as [number, number, number]);
}

export interface TenantPalette {
  /** The color the tenant chose (or pear-700). */
  brand: string;
  /** Button fills, links and focus: the brand color, darkened if white text on it would fall under 4.5:1. */
  fill: string;
  hover: string;
  /** Selected rows and soft highlights. */
  tint: string;
  /** True when `fill` had to be darkened. */
  adjusted: boolean;
  /** White-on-brand contrast before any adjustment. */
  ratio: number;
}

export function tenantPalette(primary: string | null | undefined): TenantPalette {
  if (!primary || !isHexColor(primary) || primary.toUpperCase() === PEAR_700) {
    return { brand: PEAR_700, fill: PEAR_700, hover: PEAR_500, tint: PEAR_50, adjusted: false, ratio: contrast(PEAR_700, WHITE) };
  }
  const brand = primary.toUpperCase();
  const ratio = contrast(brand, WHITE);
  const fill = ratio >= MIN_CONTRAST ? brand : darkenForText(brand);
  // Hover lightens slightly when that still carries white text, otherwise deepens.
  const lighter = mix(fill, 0.85);
  const hover = contrast(lighter, WHITE) >= MIN_CONTRAST ? lighter : darkenForText(mix(fill, 0.9, '#000000'), WHITE, MIN_CONTRAST);
  return { brand, fill, hover, tint: mix(brand, 0.1), adjusted: fill !== brand, ratio };
}
